import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema, serviceSchema } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { FaqList } from "../components/site/FaqList";
import { CtaBand, CtaCluster } from "../components/site/Cta";
import { OpenBadge } from "../components/site/OpenBadge";
import { CoverageCheck } from "../components/forms/CoverageCheck";
import { byKey } from "../data/services";
import { SITE, TEL } from "../lib/site";

function Aside({ s }) {
  return (
    <aside className="lg:sticky lg:top-28">
      <div className="rounded-[1.75rem] bg-ag-ink p-7 text-white">
        <OpenBadge dark testid="service-aside-open-badge" />
        <p className="mt-5 font-display text-2xl font-extrabold leading-tight">Une question sur votre véhicule ?</p>
        <a href={TEL} className="btn btn-blue mt-6 w-full justify-center" data-testid="service-aside-call"><Phone className="h-4 w-4" />{SITE.phone}</a>
        {s.cta.to.startsWith("#")
          ? <a href={s.cta.to} className="btn btn-yellow mt-3 w-full justify-center" data-testid="service-aside-cta">{s.cta.label}</a>
          : <Link to={s.cta.to} className="btn btn-yellow mt-3 w-full justify-center" data-testid="service-aside-cta">{s.cta.label}</Link>}
        <p className="mt-5 text-xs text-slate-400">{SITE.street}, {SITE.zip} {SITE.city}</p>
      </div>
    </aside>
  );
}

function Steps({ steps }) {
  return (
    <div className="mt-16">
      <h2 className="font-display text-3xl font-extrabold tracking-tight text-ag-ink">Comment ça se passe ?</h2>
      <ol className="mt-8 grid gap-3 sm:grid-cols-2">
        {steps.map((t, i) => (
          <Reveal as="li" key={t} delay={i * 0.05} className="flex gap-4 rounded-2xl bg-ag-mist p-5 ring-1 ring-ag-blue/10">
            <span className="font-mono text-sm font-semibold text-ag-blue">0{i + 1}</span>
            <span className="font-semibold text-ag-ink">{t}</span>
          </Reveal>
        ))}
      </ol>
    </div>
  );
}

function Related({ keys }) {
  return (
    <section className="px-5 pb-8 lg:px-8" data-testid="related-services">
      <div className="mx-auto max-w-[1320px]">
        <h2 className="font-display text-2xl font-extrabold text-ag-ink">Nos autres services à Haguenau</h2>
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          {keys.map(byKey).map((r) => (
            <Link key={r.key} to={r.path} className="group flex items-center justify-between rounded-2xl bg-white p-6 ring-1 ring-slate-200 transition-colors hover:ring-ag-blue/40" data-testid={`related-${r.key}`}>
              <span className="font-display text-lg font-bold text-ag-ink">{r.nav}</span>
              <ArrowUpRight className="h-5 w-5 text-ag-blue transition-transform group-hover:rotate-45" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ServicePage({ k }) {
  const s = byKey(k);
  const crumbs = [{ to: "/", label: "Accueil" }, { to: s.path, label: s.nav }];
  return (
    <>
      <Seo title={s.title} description={s.description} path={s.path} image={s.image} schemas={[serviceSchema(s), faqSchema(s.faq), breadcrumbSchema(crumbs)]} />
      <PageHero eyebrow={s.eyebrow} title={s.h1} intro={s.intro} crumbs={crumbs} image={s.image} imageAlt={s.imageAlt}>
        <CtaCluster prefix="service-hero" />
      </PageHero>
      <section className="px-5 py-20 lg:px-8">
        <div className="mx-auto grid max-w-[1320px] gap-12 lg:grid-cols-12">
          <article className="lg:col-span-8" data-testid="service-content">
            {s.sections.map((sec) => (
              <Reveal key={sec.h2} className="mb-12">
                <h2 className="font-display text-3xl font-extrabold tracking-tight text-ag-ink">{sec.h2}</h2>
                {sec.p.map((p) => <p key={p.slice(0, 24)} className="mt-4 text-base leading-relaxed text-slate-700 md:text-lg">{p}</p>)}
                {sec.link && <Link to={sec.link.to} className="mt-4 inline-flex items-center gap-2 font-semibold text-ag-blue">{sec.link.label} <ArrowRight className="h-4 w-4" /></Link>}
              </Reveal>
            ))}
            {k === "domicile" && <CoverageCheck />}
            <Steps steps={s.steps} />
            <div className="mt-16">
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-ag-ink">Questions fréquentes</h2>
              <div className="mt-6"><FaqList items={s.faq} prefix={`${k}-faq`} /></div>
            </div>
          </article>
          <div className="lg:col-span-4"><Aside s={s} /></div>
        </div>
      </section>
      <Related keys={s.related} />
      <CtaBand />
    </>
  );
}
